import Phaser from 'phaser';
import { locations } from '@/data/locations';
import type { Location } from '@/data/types';
import { GameState } from '@/engine/GameState';
import {
  ensureRunning,
  goTo,
  SCENE_KEYS,
  ZONE_SCENE_MAP,
  type PlaceholderSceneData,
} from '@/engine/nav';

const COLOR_FONDO = 0x14141c;
const COLOR_PANEL = 0x22222e;
const COLOR_PANEL_HOVER = 0x30303f;
// Color apagado para zonas bloqueadas por umbral de respeto (beta: 0 en
// todas las zonas, pero la lógica queda lista para cuando se suba el listón).
const COLOR_PANEL_BLOQUEADO = 0x181820;
const COLOR_TEXTO_BLOQUEADO = '#6c6c78';
const HUD_ALTO = 100; // debe coincidir con HudScene: deja hueco arriba para no tapar el HUD
const MARGEN = 24;

/**
 * Hub de barrio: mapa vertical tocable con las 5 zonas de la beta. Cada
 * zona es un panel táctil grande con emoji + nombre (datos leídos de
 * src/data/locations.ts, cero texto narrativo aquí). Tocar una zona navega
 * a PlaceholderScene pasándole el id/nombre de la zona.
 */
export class HubScene extends Phaser.Scene {
  constructor() {
    super(SCENE_KEYS.HUB);
  }

  create(): void {
    const { width, height } = this.scale;
    this.cameras.main.setBackgroundColor(COLOR_FONDO);

    ensureRunning(this, SCENE_KEYS.HUD);
    this.scene.bringToTop(SCENE_KEYS.HUD);

    this.add
      .text(width / 2, HUD_ALTO + 36, 'EL BARRIO', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '22px',
        color: '#9a9aa5',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    const zonasDisponibles = locations; // en la beta las 5 están desbloqueadas desde el inicio
    const areaSuperior = HUD_ALTO + 70;
    const areaInferior = height - MARGEN;
    const altoDisponible = areaInferior - areaSuperior;
    const altoPanel = (altoDisponible - MARGEN * (zonasDisponibles.length - 1)) / zonasDisponibles.length;
    const anchoPanel = width - MARGEN * 2;

    zonasDisponibles.forEach((zona, i) => {
      const y = areaSuperior + i * (altoPanel + MARGEN) + altoPanel / 2;
      this.crearPanelZona(zona, width / 2, y, anchoPanel, altoPanel);
    });
  }

  private crearPanelZona(zona: Location, x: number, y: number, ancho: number, alto: number): void {
    // Estado de desbloqueo: comparamos contra el respeto vivo (no el
    // snapshot) para reflejar cambios al volver al hub tras una misión. En la
    // beta todas las zonas tienen umbral 0, así que el camino "bloqueado"
    // queda latente pero preparado.
    const respeto = GameState.respeto;
    const bloqueada = respeto < zona.umbralRespetoDesbloqueo;

    const panel = this.add.graphics();
    const dibujar = (color: number) => {
      panel.clear();
      panel.fillStyle(color, 1);
      panel.fillRoundedRect(-ancho / 2, -alto / 2, ancho, alto, 18);
    };
    dibujar(bloqueada ? COLOR_PANEL_BLOQUEADO : COLOR_PANEL);
    panel.setPosition(x, y);

    this.add
      .text(x - ancho / 2 + 28, y, zona.emoji, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '40px',
      })
      .setOrigin(0, 0.5);

    this.add
      .text(x - ancho / 2 + 90, y, zona.nombre, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '24px',
        fontStyle: 'bold',
        color: bloqueada ? COLOR_TEXTO_BLOQUEADO : '#ffffff',
        wordWrap: { width: ancho - 120 },
      })
      .setOrigin(0, 0.5);

    // Mensaje de "necesitas X respeto" pegado bajo el nombre: visible solo si
    // la zona está bloqueada. No bloquea la partida, solo informa.
    if (bloqueada) {
      this.add
        .text(x - ancho / 2 + 90, y + 26, `🔒 Respeto ${zona.umbralRespetoDesbloqueo} necesario`, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '14px',
          color: COLOR_TEXTO_BLOQUEADO,
        })
        .setOrigin(0, 0.5);
    }

    const zonaTactil = this.add
      .zone(x, y, ancho, alto)
      .setInteractive({ useHandCursor: true });

    if (bloqueada) {
      // Sin hover ni navegación: el tap "rebota" en silencio (placeholder
      // para v2: shake o micro-feedback cuando exista animación de bloqueo).
      return;
    }

    zonaTactil.on('pointerover', () => dibujar(COLOR_PANEL_HOVER));
    zonaTactil.on('pointerout', () => dibujar(COLOR_PANEL));
    zonaTactil.on('pointerdown', () => dibujar(COLOR_PANEL_HOVER));
    zonaTactil.on('pointerup', () => {
      // Si la zona tiene escena propia en el mapa, vamos a ella; si no,
      // caemos al PLACEHOLDER genérico.
      const escenaPropia = ZONE_SCENE_MAP[zona.id];
      if (escenaPropia) {
        goTo(this, escenaPropia);
        return;
      }
      const data: PlaceholderSceneData = { zoneId: zona.id, zoneName: zona.nombre };
      goTo(this, SCENE_KEYS.PLACEHOLDER, data);
    });
  }
}
